import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Warning, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Configura la actividad AFIP (CLAE) que se envía al asociar un REC 995/996
 * a la Factura A. Sin este dato, AFIP rechaza con error 10228 según RG 5259/2022.
 */
export default function ActividadAFIPTab() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actividades, setActividades] = useState([]);
  const [seleccionada, setSeleccionada] = useState("");
  const [errores, setErrores] = useState([]);

  const cargar = async () => {
    setLoading(true);
    try {
      const [{ data: acts }, { data: cfg }] = await Promise.all([
        api.get("/afip/actividades"),
        api.get("/afip/settings"),
      ]);
      setActividades(acts.actividades || []);
      setErrores(acts.errores || []);
      setSeleccionada(cfg.actividad_carnica_id ? String(cfg.actividad_carnica_id) : "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron cargar las actividades AFIP");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    if (!seleccionada) { toast.error("Elegí una actividad"); return; }
    setSaving(true);
    try {
      await api.post("/afip/settings", { actividad_carnica_id: parseInt(seleccionada, 10) });
      toast.success("Actividad AFIP guardada. Ya podés facturar con REC asociado.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const configurada = !!seleccionada;

  return (
    <div className="space-y-6" data-testid="afip-actividad-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {configurada ? (
              <>
                <CheckCircle size={20} weight="duotone" className="text-emerald-600" />
                Actividad AFIP para REC · Configurada
              </>
            ) : (
              <>
                <Warning size={20} weight="duotone" className="text-amber-600" />
                Actividad AFIP para REC · Sin configurar
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p className="text-neutral-600 leading-relaxed">
            AFIP requiere informar la <b>actividad cárnica</b> (código CLAE) cuando la Factura A
            va asociada a un Remito Electrónico Cárnico (995/996). Este dato es obligatorio
            según RG 5259/2022; si no se envía, AFIP rechaza la factura con el error{" "}
            <code className="bg-neutral-100 px-1 rounded font-mono text-xs">10228</code>.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-end">
            <div className="lg:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-500">
                Actividad habilitada del emisor
              </Label>
              <Select value={seleccionada} onValueChange={setSeleccionada} disabled={loading}>
                <SelectTrigger data-testid="afip-actividad-select">
                  <SelectValue placeholder={loading ? "Cargando desde AFIP…" : "Elegí una actividad…"} />
                </SelectTrigger>
                <SelectContent>
                  {actividades.map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      <span className="font-mono text-xs text-neutral-500 mr-2">{a.id}</span>
                      {a.descripcion}
                      {a.orden === 1 && (
                        <Badge variant="secondary" className="ml-2 text-[10px]">Primaria</Badge>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-neutral-500 mt-1">
                Sugerido para frigoríficos: elegí la actividad cárnica correspondiente
                (mayorista/minorista de carnes rojas u otra según tu inscripción).
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={cargar}
                disabled={loading}
                data-testid="afip-actividad-reload"
              >
                <ArrowClockwise size={14} className="mr-1" />
                Refrescar
              </Button>
              <Button
                onClick={guardar}
                disabled={saving || !seleccionada}
                data-testid="afip-actividad-save"
              >
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </div>

          {errores.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
              <b>AFIP devolvió errores:</b>
              <ul className="list-disc list-inside mt-1">
                {errores.map((e, i) => <li key={i}>{String(e)}</li>)}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
